<script setup>
  import useVuelidate from '@vuelidate/core'

  import { computed } from 'vue'

  import FieldRendererLabel from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererLabel.vue'
  import FieldRendererValidationMessages from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererValidationMessages.vue'
  import FieldRendererWrapper from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererWrapper.vue'
  import { useFieldPlugins } from '@/features/settingsManagement/fieldManagement/edit/plugin/fieldsPlugin.js'
  import {
    fieldCommonProps,
    normalizeFieldExtraData,
  } from '@/features/settingsManagement/fieldManagement/edit/utils/fieldUtils.js'
  import FormCheckbox from '@/features/ui/components/forms/FormCheckbox.vue'
  import { mergeDeep } from '@/features/utils/object.utils.js'

  const emit = defineEmits(['update:modelValue'])

  const props = defineProps({
    ...fieldCommonProps,
    modelValue: {
      type: [Boolean, null],
      default: null,
    },
  })

  const { getPlugin } = useFieldPlugins()
  const plugin = getPlugin('checkbox')

  const extraData = computed(() => {
    return mergeDeep(plugin.defaultExtraData, normalizeFieldExtraData(props.field.extraData))
  })

  const rules = computed(() => ({
    value: plugin.getValidations({ props }),
  }))

  const state = computed(() => ({
    value: props.modelValue,
  }))

  const v$ = useVuelidate(rules, state)

  async function validate() {
    const result = await v$.value.$validate()
    return {
      isValid: result,
      errors: Object.entries(v$.value.$errors).map(([, error]) => error.$message),
    }
  }

  function updateValue(value) {
    emit('update:modelValue', value)
  }

  const trueValue = computed(() => extraData.value.trueValue)
  const falseValue = computed(() => extraData.value.falseValue)

  defineExpose({
    validate,
  })
</script>

<template>
  <FieldRendererWrapper :field="field">
    <template #label>
      <FieldRendererLabel :field="field" :external-required="externalRequired" />
    </template>

    <FormCheckbox
      :model-value="modelValue"
      :binary="true"
      :true-value="trueValue"
      :false-value="falseValue"
      :disabled="disabled"
      :invalid="v$.value.$error || hasExternalError"
      @update:model-value="updateValue"
    />

    <template #validation>
      <FieldRendererValidationMessages v-if="v$.value.$error" :error-messages="v$.value.$errors" />
    </template>
  </FieldRendererWrapper>
</template>
